import axios from 'axios';
import API_CONFIG from '../config/api';
import { getAuthToken, setAuthToken } from './tokenManager';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  withCredentials: true, // Important: enables cookie sending/receiving
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Callback for handling session expiration globally
let onUnauthorized = null;
export const setUnauthorizedHandler = (handler) => { onUnauthorized = handler; };

// Request interceptor - no longer needed to manually add tokens since they're in cookies
apiClient.interceptors.request.use(
  (config) => {
    // Web: getAuthToken() is null → no header, the httpOnly cookie carries auth.
    // Native (Capacitor): the webview is cross-site to the API so the cookie is
    // never sent; attach the stored JWT as a Bearer header instead.
    const token = getAuthToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // Sliding session (native/Bearer only): the server re-issues a token once
    // a day on X-Refreshed-Token; persist it so the app stays signed in. On
    // web the refreshed httpOnly cookie arrives on its own and this is a no-op.
    const refreshed = response?.headers?.['x-refreshed-token'];
    if (refreshed && getAuthToken()) {
      setAuthToken(refreshed).catch(() => {});
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401 && onUnauthorized) {
      const url = error.config?.url || '';
      // Don't trigger logout for login/register attempts (those 401s are wrong-password errors)
      if (!url.includes('/auth/login') && !url.includes('/auth/register')) {
        onUnauthorized();
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
